import { Icon } from '@acme/assets';
import { useUnit } from 'effector-react';
import React, { useCallback, useRef, useState } from 'react';
import { $isConnected } from '../../../shared/messageHub/model';
import { useChatWindowModel } from '../lib/chatwindowcontext';

export const MessageInput: React.FC<{ disabled?: boolean }> = ({
  disabled = false,
}) => {
  const model = useChatWindowModel();
  const isConnected = useUnit($isConnected);
  const isSending = useUnit(model.sendMessageFx.pending);
  const [inputText, setInputText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      setSelectedFiles((prev) => [...prev, ...files]);

      // Сбрасываем значение input, чтобы можно было выбрать тот же файл снова
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    []
  );

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSendMessage = async () => {
    if (
      (!inputText.trim() && selectedFiles.length === 0) ||
      !isConnected ||
      isSending
    ) {
      return;
    }

    try {
      const messageRequest = {
        formData: {
          text: inputText.trim(),
          files: selectedFiles.length > 0 ? selectedFiles : undefined,
        },
      };

      model.sendMessage(messageRequest);

      // Очищаем форму после успешной отправки
      setInputText('');
      setSelectedFiles([]);
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const isSendDisabled =
    disabled ||
    !isConnected ||
    isSending ||
    (!inputText.trim() && selectedFiles.length === 0);

  return (
    <>
      {/* Список выбранных файлов */}
      {selectedFiles.length > 0 && (
        <div className="px-4 pb-3 border-b border-gray-100">
          <div className="flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center bg-gray-50 rounded-lg px-3 py-2 text-sm"
              >
                <span className="font-medium truncate max-w-[120px]">
                  {file.name}
                </span>
                <button
                  onClick={() => removeFile(index)}
                  className="ml-2 text-gray-700 hover:text-gray-900 text-xs"
                  type="button"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-3 items-center">
        {/* Кнопка выбора файлов */}
        <button
          type="button"
          onClick={openFilePicker}
          disabled={!isConnected || disabled}
          className=" text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50 flex-shrink-0"
          title="Прикрепить файл"
        >
          <Icon name="plus-outline" className="text-primary-500" />
        </button>

        {/* Скрытый input для файлов */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept="*/*" // Можно ограничить типы файлов: "image/*,.pdf,.doc,.docx"
          disabled={!isConnected || disabled}
        />

        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={isConnected ? 'Введите сообщение...' : 'Подключение...'}
          disabled={!isConnected}
          className="flex-1 border border-gray-300 text-gray-900 rounded-lg px-4 py-3 text-sm focus:border-brand-purple-900"
        />
        <button
          className={`rounded-full flex w-7 h-7 justify-center items-center transition-colors ${
            isSendDisabled
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-brand-purple-900 text-white cursor-pointer hover:bg-brand-purple-800'
          }`}
          disabled={isSendDisabled}
          onClick={handleSendMessage}
        >
          <Icon name="arrow-up-outline" />
        </button>
      </div>
    </>
  );
};
